import { useState, useEffect } from 'react';
import {
  PublicClientApplication,
  LogLevel,
  InteractionRequiredAuthError,
} from '@azure/msal-browser';
import { Button, Layout } from 'antd';
import ScaledLayout from './components/Layout/ScaleLayout';
import SideNav from './components/Layout/SideNav';
import PSOPanel from './components/PSOPanel';
import { config } from './config';
import './App.css';

const publicClientApplication = new PublicClientApplication({
  auth: {
    clientId: config.appId,
    redirectUri: config.redirectUrl,
    authority: config.authority,
    navigateToLoginRequestUrl: true,
  },
  cache: {
    cacheLocation: 'localStorage',
  },
  system: {
    loggerOptions: {
      logLevel: LogLevel.Verbose,
      piiLoggingEnabled: true,
      loggerCallback: (level, message, containsPii) => {
        switch (level) {
          case LogLevel.Error:
            console.error(message);
            return;
          case LogLevel.Info:
            console.info(message);
            return;
          case LogLevel.Verbose:
            console.debug(message);
            return;
          case LogLevel.Warning:
            console.warn(message);
            return;
          default:
            return;
        }
      },
    },
  },
});

const { Content } = Layout;

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userInfo, setUserInfo] = useState({});
  const [currentView, setCurrentView] = useState('main');

  const handleMenuSelect = (key) => {
    setCurrentView(key);
  };

  const logout = async () => {
    const activeAccount = publicClientApplication.getActiveAccount();
    try {
      await publicClientApplication.logoutPopup({
        account: activeAccount ?? undefined,
      });
    } catch (err) {
      console.error('Logout failed:', err);
    } finally {
      setIsAuthenticated(false);
      setUserInfo({});
      setCurrentView('main');
    }
  };

  const login = async () => {
    try {
      await publicClientApplication
        .loginPopup({
          scopes: config.scopes,
          prompt: 'select_account',
        })
        .then((res) => {
          if (res.idTokenClaims && 'groups' in res.idTokenClaims) {
            setIsAuthenticated(true);
            setUserInfo({
              username: res?.account?.username,
              tenantId: res?.account?.tenantId,
              name: res?.account?.name,
            });
          } else {
            alert("You don't have access to PSO panel");
          }
        });
    } catch (err) {
      setIsAuthenticated(false);
    }
  };

  useEffect(() => {
    const currentAccounts = publicClientApplication.getAllAccounts();

    if (currentAccounts.length > 0) {
      publicClientApplication.setActiveAccount(currentAccounts[0]);

      const urlParams = new URLSearchParams(window.location.search);
      const userId = urlParams.get('name');

      // If URL has ?name= param, verify it matches the cached account
      if (userId && !currentAccounts[0].username?.includes(userId)) {
        setIsAuthenticated(false);
        return;
      }

      const silentRequest = {
        scopes: ['user.read'],
        account: currentAccounts[0],
        forceRefresh: false,
      };

      const applyToken = (tokenResponse) => {
        if (
          tokenResponse.idTokenClaims &&
          'groups' in tokenResponse.idTokenClaims
        ) {
          setIsAuthenticated(true);
          setUserInfo({
            username: currentAccounts[0].username,
            tenantId: currentAccounts[0].tenantId,
            name: currentAccounts[0].name,
          });
        } else {
          alert("You don't have access to PSO panel");
        }
      };

      publicClientApplication
        .acquireTokenSilent(silentRequest)
        .then(applyToken)
        .catch((error) => {
          // SPA refresh tokens expire after 24h and cannot be renewed
          // silently. Fall back to an interactive popup so the user gets a
          // fresh token without a full re-login.
          if (error instanceof InteractionRequiredAuthError) {
            publicClientApplication
              .acquireTokenPopup({
                scopes: silentRequest.scopes,
                account: currentAccounts[0],
              })
              .then(applyToken)
              .catch((popupError) => {
                console.error(
                  'Interactive token acquisition failed:',
                  popupError
                );
                setIsAuthenticated(false);
              });
          } else {
            console.error('Silent token acquisition failed:', error);
            setIsAuthenticated(false);
          }
        });
    } else {
      setIsAuthenticated(false);
    }
  }, []);

  return (
    <div>
      {isAuthenticated ? (
        <Layout style={{ minHeight: '100vh' }}>
          <SideNav
            onMenuSelect={handleMenuSelect}
            onLogout={logout}
            selectedKey={currentView}
          />
          <Layout style={{ marginLeft: '130px' }}>
            <Content style={{ height: '100vh', overflow: 'hidden' }}>
              <ScaledLayout scale={0.8}>
                <PSOPanel userInfo={userInfo} currentView={currentView} />
              </ScaledLayout>
            </Content>
          </Layout>
        </Layout>
      ) : (
        <div className="loginContainer">
          <Button type="primary" onClick={login}>
            Login in to MED Dashboard
          </Button>
        </div>
      )}
    </div>
  );
}

export default App;
